/**
 * The practice-forms panel in the patient file — `tasks/patient-forms.md` §5 host 3, §5.2.
 *
 * Sits directly under CONSENT in `patient-file.component.html`, because a form request is the same
 * kind of thing as a consent request and that is where a user goes looking for it.
 *
 * ── THE ANSWERS ARE RENDERED BY THE SHARED RENDERER, NOT BY THIS COMPONENT ───────────────────
 *
 * Expanding a completed row draws `<app-dynamic-form-renderer [readonly]="true">` with the
 * schema of the version the patient was ACTUALLY GIVEN (`CustomFormVersion.schema`, pinned at
 * issue time — §1.2 defect 5), not the form's current version. §5 is "one renderer, three hosts"
 * and this is the third: a hand-rolled answer table here would be the second implementation of
 * option-text lookup, `yesno` display and signature rendering, and it would drift.
 *
 * ── THERE IS NO COPY-LINK BUTTON, AND THAT IS DELIBERATE ─────────────────────────────────────
 *
 * §4.1 stores only `sha256(token)`, so the raw link is unrecoverable by anything, including the
 * server. §14.3 decision 2: RESEND MINTS A NEW TOKEN and the previously sent link 404s the moment
 * it does. That is a real behavioural difference from `resendInvite`, which re-sends the same
 * link, so the resend confirmation says so out loud — a receptionist who resends and then tells
 * the patient "use whichever email you like" would be wrong. `tokenDigest` never reaches the
 * client (`toStaffDto` deletes it) and nothing here renders one.
 *
 * ── `in_progress` IS SHOWN DISTINCTLY ON PURPOSE ─────────────────────────────────────────────
 *
 * It means the patient opened the link and their draft is saved. That is actionable in a way
 * `pending` is not: a reminder to somebody who started is likely to work, and a form stuck at
 * `in_progress` for a week is a form that is too long.
 */

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { interval, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import {
  formatCooldownRemaining,
  getResendCooldown,
} from '../../../../core/services/patient-invite.service';
import {
  PatientFormRequestDto,
  PatientFormRequestService,
  PatientFormRequestStatus,
  describeSendFailure,
} from '../../../../core/services/patient-form-request.service';
import { PatientService } from '../../../../core/services/patient.service';
import { FormAnswers, FormSchema } from '../../../../shared/models/custom-form.model';
import { PatientFormSendDialogComponent } from './patient-form-send-dialog.component';
import { inject } from '@angular/core';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

/** Wording aimed at a practice user. `pending` is not a word a receptionist uses. */
const STATUS_LABELS: { [key: string]: string } = {
  pending: 'Awaiting patient',
  in_progress: 'Started',
  completed: 'Completed',
  expired: 'Link expired',
  revoked: 'Cancelled',
};

@Component({
  selector: 'app-patient-form-list',
  templateUrl: './patient-form-list.component.html',
  styleUrls: ['./patient-form-list.component.scss'],
})
export class PatientFormListComponent extends BaseComponent implements OnInit {
  private readonly groupService = inject(GroupService);

  /** A state unit (tasks/state-mode.md): no form is sent to the patient. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }


  /** The panel contract every sibling in the accordion uses. Do not change its shape. */
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  /**
   * So `focusRequest` can OPEN the panel. `[expanded]` is bound to `initialExpanded` exactly as
   * every sibling panel binds it — a one-way input the parent owns — so the only honest way to
   * open ourselves is to ask the panel, which then emits `(opened)` and keeps the parent in step.
   */
  @ViewChild(MatExpansionPanel) panel?: MatExpansionPanel;

  /** `null` while the first load is in flight; `[]` once we know there are none. */
  requests: PatientFormRequestDto[] | null = null;

  /** Which row is open. One at a time — the answers of two forms side by side help nobody. */
  expandedRequestId: string | null = null;

  /** `GET /patient-form-requests/:id` results, keyed by request id. */
  details: { [requestId: string]: PatientFormRequestDto } = {};
  detailLoading: { [requestId: string]: boolean } = {};
  detailError: { [requestId: string]: string } = {};

  /** Ticks every second so the resend cooldown countdown moves. Same idiom as the consent panel. */
  cooldownTick = 0;

  busyRequestId: string | null = null;

  constructor(
    private patientFormRequestService: PatientFormRequestService,
    private patientService: PatientService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.patientFormRequestService.patientFormRequests$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (requests) => {
          this.requests = requests;
          // A row that has gone away (patient switched) must not keep a stale detail open.
          if (this.expandedRequestId && requests
            && !requests.some((r) => r.id === this.expandedRequestId)) {
            this.expandedRequestId = null;
          }
        },
        error: (error: any) => {
          console.error('Error loading patient form requests: ', error);
          this.requests = [];
        },
      });

    // The send dialog asks for this when a dedupe 409 named the request that blocked the send.
    this.patientFormRequestService.focusRequest$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => this.focusRequest(id));

    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cooldownTick = Date.now();
    });
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  /* ── Row presentation ─────────────────────────────────────────────────────────────────── */

  formName(request: PatientFormRequestDto): string {
    return (request.CustomForm && request.CustomForm.name) || 'Form';
  }

  statusLabel(request: PatientFormRequestDto): string {
    const label = STATUS_LABELS[request.status];
    return label ? label : request.status;
  }

  /** Drives the chip colour. One class per status — the five are visually distinct. */
  statusClass(request: PatientFormRequestDto): string {
    return `status-${request.status}`;
  }

  isStatus(request: PatientFormRequestDto, status: PatientFormRequestStatus): boolean {
    return request.status === status;
  }

  /** When the patient was last asked — a resend moves this, which is what the practice wants. */
  sentAt(request: PatientFormRequestDto): string | null | undefined {
    return request.lastSentAt || request.createdAt;
  }

  /**
   * `12 / 20`, or `''`.
   *
   * A form with no scored options anywhere has `scoreTotal`/`scoreMax` NULL, and §13.1 is explicit
   * that "unscored" and "scored zero" are different facts — so an unscored form shows no score at
   * all rather than `0 / 0`.
   */
  scoreLabel(request: PatientFormRequestDto): string {
    const total = request.scoreTotal;
    const max = request.scoreMax;
    if (total === null || total === undefined || max === null || max === undefined) return '';
    return `${total} / ${max}`;
  }

  hasScore(request: PatientFormRequestDto): boolean {
    return this.scoreLabel(request) !== '';
  }

  /* ── Expanding a row ──────────────────────────────────────────────────────────────────── */

  /** Only a completed row has anything to open. */
  canOpen(request: PatientFormRequestDto): boolean {
    return request.status === 'completed';
  }

  isOpen(request: PatientFormRequestDto): boolean {
    return this.expandedRequestId === request.id;
  }

  toggleRow(request: PatientFormRequestDto): void {
    if (!this.canOpen(request)) return;
    if (this.expandedRequestId === request.id) {
      this.expandedRequestId = null;
      return;
    }
    this.expandedRequestId = request.id;
    this.loadDetail(request.id);
  }

  private loadDetail(id: string): void {
    if (this.details[id] || this.detailLoading[id]) return;
    this.detailLoading[id] = true;
    delete this.detailError[id];

    this.patientFormRequestService.loadRequestById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.detailLoading[id] = false;
          if (result && result.data) {
            this.details[id] = result.data;
          } else {
            this.detailError[id] = 'These answers could not be loaded.';
          }
        },
        error: (error: any) => {
          this.detailLoading[id] = false;
          console.error('Error loading form request detail: ', error);
          this.detailError[id] = 'These answers could not be loaded. Please try again.';
        },
      });
  }

  /**
   * THE SCHEMA THE PATIENT WAS ACTUALLY GIVEN. Never `CustomForm.currentVersionId` — publishing v2
   * must not change how last month's answers read.
   */
  schemaFor(request: PatientFormRequestDto): FormSchema | null {
    const detail = this.details[request.id];
    if (!detail || !detail.CustomFormVersion) return null;
    return detail.CustomFormVersion.schema || null;
  }

  /** Never null: `DynamicFormRendererComponent.value` is `FormAnswers`, not `FormAnswers | null`. */
  answersFor(request: PatientFormRequestDto): FormAnswers {
    const detail = this.details[request.id];
    return (detail && detail.answers) ? detail.answers : {};
  }

  versionLabel(request: PatientFormRequestDto): string {
    const detail = this.details[request.id];
    if (!detail || !detail.CustomFormVersion) return '';
    return `Version ${detail.CustomFormVersion.versionNumber}`;
  }

  /* ── Resend ───────────────────────────────────────────────────────────────────────────── */

  /**
   * The server refuses a resend for `completed` (ALREADY_COMPLETED) and `revoked` (REVOKED).
   * `expired` IS resendable — the resend refreshes the expiry along with the token, which is
   * precisely how a practice revives a link the patient never got round to opening.
   */
  canResend(request: PatientFormRequestDto): boolean {
    return !this.stateMode && request.status !== 'completed' && request.status !== 'revoked';
  }

  /** False while the invite-flow cooldown (`nextAllowedResendAt`) is still running. */
  resendAllowed(request: PatientFormRequestDto): boolean {
    return getResendCooldown(request.nextAllowedResendAt).canResend;
  }

  resendTooltip(request: PatientFormRequestDto): string {
    const cooldown = getResendCooldown(request.nextAllowedResendAt);
    if (cooldown.canResend) return 'Send this form to the patient again';
    return `Resend available in ${formatCooldownRemaining(cooldown.secondsRemaining)}`;
  }

  async resend(request: PatientFormRequestDto, event?: MouseEvent): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canResend(request) || this.busyRequestId) return;

    if (!this.resendAllowed(request)) {
      this.showToastMessage(this.resendTooltip(request));
      return;
    }

    // Say the quiet part. §14.3 decision 2 makes the old link die the instant this succeeds, and a
    // patient holding two emails must use the newer one.
    const confirmed = await this.showConfirmationDialog(
      'RESEND FORM',
      `Send "${this.formName(request)}" to this patient again? A new link is created and the `
      + `link that was sent before will stop working immediately.`,
      undefined,
      'Cancel',
      'Resend',
    );
    if (!confirmed) return;

    const channels = this.defaultChannels();
    this.busyRequestId = request.id;

    this.patientFormRequestService.resendRequest(request.id, channels)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyRequestId = null;
          this.showToastMessage(
            `"${this.formName(request)}" was sent again. The previous link no longer works.`);
        },
        error: (error: any) => {
          this.busyRequestId = null;
          const failure = describeSendFailure(error, this.formName(request));
          // A cooldown 409 carries the moment the button becomes usable — write it onto the row so
          // the button disables itself and the countdown is truthful.
          if (failure.nextAllowedResendAt) {
            request.nextAllowedResendAt = failure.nextAllowedResendAt;
          }
          this.showToastMessage(failure.message);
        },
      });
  }

  /** Email when we have one, SMS when we do not — the same choice the send dialog defaults to. */
  private defaultChannels(): string[] {
    const patient = this.patientService.patient;
    const channels: string[] = [];
    if (patient && patient.email) channels.push('email');
    if (!channels.length && patient && patient.mobileNumber) channels.push('sms');
    return channels.length ? channels : ['email'];
  }

  /* ── Revoke ───────────────────────────────────────────────────────────────────────────── */

  /** A completed form cannot be revoked — the answers are already in the chart (409). */
  canRevoke(request: PatientFormRequestDto): boolean {
    return request.status !== 'completed' && request.status !== 'revoked';
  }

  async revoke(request: PatientFormRequestDto, event?: MouseEvent): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canRevoke(request) || this.busyRequestId) return;

    const confirmed = await this.showConfirmationDialog(
      'CANCEL FORM REQUEST',
      `Cancel "${this.formName(request)}"? The patient's link stops working straight away.`,
      undefined,
      'Keep it',
      'Cancel request',
      true,
    );
    if (!confirmed) return;

    this.busyRequestId = request.id;

    this.patientFormRequestService.revokeRequest(request.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyRequestId = null;
          this.showToastMessage('That form request was cancelled.');
        },
        error: (error: any) => {
          this.busyRequestId = null;
          const failure = describeSendFailure(error, this.formName(request));
          this.showToastMessage(failure.message);
          // ALREADY_COMPLETED means our copy of the row is stale — the patient submitted while
          // this screen was open. Re-read rather than leaving a Cancel button on a done form.
          if (failure.code === 'ALREADY_COMPLETED') this.patientFormRequestService.refresh();
        },
      });
  }

  /* ── Send ─────────────────────────────────────────────────────────────────────────────── */

  openSendDialog(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    const patient = this.patientService.patient;
    if (!patient) {
      this.showToastMessage('Open a patient before sending a form.');
      return;
    }

    // No `afterClosed` handling: a dedupe refusal announces itself through
    // `PatientFormRequestService.focusRequest$`, which this panel is already subscribed to, so the
    // sidebar action gets the same behaviour without the patient file knowing about it.
    this.dialog.open(PatientFormSendDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: { patient },
    });
  }

  /** Open the panel and the named row. Used by the dedupe "show that request" affordance. */
  private focusRequest(id: string): void {
    if (!id) return;
    // `open()` is a no-op on an already-open panel and emits `(opened)` otherwise, which routes
    // through `toggleExpanded` and tells the patient file to remember the panel is open.
    this.panel?.open();
    const request = (this.requests || []).filter((r) => r.id === id)[0];
    this.expandedRequestId = id;
    if (request && this.canOpen(request)) this.loadDetail(id);
  }

  trackByRequest(_index: number, request: PatientFormRequestDto): string {
    return request.id;
  }
}
